// src/api/firmas.ts
import api from './axios';

export type PendienteRow = {
  reservaId: number;
  fecha: string; // "YYYY-MM-DD"
  horaInicio: string; // "HH:mm"
  horaFin: string; // "HH:mm"
  asignaturaId: number;
  asignatura: string;
  alumnos: string; // "Nombre1, Nombre2"
  centro: string; // "Online" o nombre
  firmasReq: number;
  profSigned: 0 | 1 | boolean; // viene como 0/1 desde SQL
  alumTotal: number;
  alumFirmados: number;
};

export type ListResp = { rows: PendienteRow[]; total: number };

export type PendingCounts = {
  total: number;
  profesor: number;
  alumnos: number;
  soloProfesor: number;
  soloAlumnos: number;
  ambos: number;
};

export async function getPendingCounts(): Promise<PendingCounts> {
  const { data } = await api.get('/firmas/mis/counts');
  return data as PendingCounts;
}

const BASE = '/firmas';

export async function getPendingCount(): Promise<number> {
  const { data } = await api.get(`${BASE}/mis/count`);
  const { n } = data ?? {};
  return Number(n ?? 0);
}

export type ListParams = {
  search?: string;
  centroId?: number;
  asignaturaId?: number;
  sort?: 'fecha_asc' | 'fecha_desc' | 'centro' | 'asignatura' | 'alumno';
  skip?: number;
  take?: number;
};

export type IncumplimientoProfesorRow = {
  profesorId: number;
  profesor: string;
  reservasSinFirmaProfesor: number;
  totalFinalizadas: number;
  porcentajeIncumplimiento: number;
  ultimaPendiente: string | null;
};

export type IncumplimientoProfesoresResp = {
  rows: IncumplimientoProfesorRow[];
  totalProfesores: number;
  reservasSinFirmaProfesor: number;
};

export type IncumplimientoReservaRow = {
  reservaId: number;
  fecha: string;
  horaInicio: string;
  horaFin: string;
  asignatura: string;
  centro: string;
  alumnos: string;
  hayAlumnosSinFirma: boolean;
};

export type IncumplimientoFilters = {
  desde?: string;
  hasta?: string;
  search?: string;
  minPendientes?: number;
};

function buildQueryString(params: Record<string, string | number | undefined>) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    qs.set(key, String(value));
  });
  return qs.toString();
}

export async function getIncumplimientoProfesores(
  filters: IncumplimientoFilters,
): Promise<IncumplimientoProfesoresResp> {
  const qs = buildQueryString(filters as Record<string, string | number | undefined>);
  const { data } = await api.get(`${BASE}/incumplimiento/profesores?${qs}`);
  return data as IncumplimientoProfesoresResp;
}

export async function getIncumplimientoReservasProfesor(
  profesorId: number,
  filters: Pick<IncumplimientoFilters, 'desde' | 'hasta'>,
): Promise<IncumplimientoReservaRow[]> {
  const qs = buildQueryString(filters as Record<string, string | number | undefined>);
  const { data } = await api.get(`${BASE}/incumplimiento/profesores/${profesorId}/reservas?${qs}`);
  return data as IncumplimientoReservaRow[];
}

export async function generateFormsForMissing(reservaId: number) {
  // Usa el endpoint ya existente que crea FORM para alumnos faltantes
  const { data } = await api.post(`/reservas/${reservaId}/firmas/links/faltantes`, {
    incluirProfesor: false, // solo alumnos
    modo: 'remoto',
  });
  // devuelve array de { idUsuario, rol, url, enlaceId }
  return Array.isArray(data) ? data : [];
}

export async function submitAlumnoForm(token: string, motivo: string) {
  // Enviar el formulario (usa el público /form-firma)
  const { data } = await api.post(`/form-firma?token=${encodeURIComponent(token)}`, { motivo });
  return data;
}
export async function getMyPendings(params: ListParams = {}): Promise<ListResp> {
  const qs = new URLSearchParams();
  if (params.search) qs.set('search', params.search);
  if (params.centroId) qs.set('centroId', String(params.centroId));
  if (params.asignaturaId) qs.set('asignaturaId', String(params.asignaturaId));
  qs.set('sort', params.sort ?? 'fecha_desc');
  qs.set('skip', String(params.skip ?? 0));
  qs.set('take', String(params.take ?? 50));

  const { data } = await api.get(`${BASE}/mis?${qs}`);
  return data as ListResp;
}
